/**
 * Keybinds, as Discord's: Toggle mute and Toggle deafen (Settings › Voice &
 * audio › Keybinds). A combination is stored as "Ctrl+Shift+KeyM": the
 * modifiers in the order Ctrl, Alt, Shift, Meta, then a `KeyboardEvent.code`
 * (layout-independent, as push-to-talk's key).
 *
 * - Web: in the focused tab. A combination with Ctrl, Alt or Meta works
 *   while typing too (it types nothing); a bare key never fires in a field.
 * - Desktop (the bridge's `setHotkeys`, while `desktop_global_ptt` is on):
 *   global, through the shell's push-to-talk hook, so a game can have focus.
 *   The window listener stays (the hook can miss our own window, as with
 *   push-to-talk), and a press both report counts once.
 */
import type { DesktopHotkeyAction, NeosocialDesktopBridge } from '@neosocial/protocol';
import { isTypingTarget, keyLabel } from './ptt';

export type HotkeyAction = DesktopHotkeyAction;
export const HOTKEY_ACTIONS: readonly HotkeyAction[] = ['toggle_mute', 'toggle_deafen'];
export type HotkeyKeys = Record<HotkeyAction, string | null>;

export interface Combo {
  ctrl: boolean;
  alt: boolean;
  shift: boolean;
  meta: boolean;
  code: string;
}

const MODIFIER_CODES = new Set([
  'ControlLeft',
  'ControlRight',
  'ShiftLeft',
  'ShiftRight',
  'AltLeft',
  'AltRight',
  'MetaLeft',
  'MetaRight',
  'OSLeft',
  'OSRight',
]);

/** "Ctrl+Shift+KeyM" → its parts; null for an empty or malformed one. */
export function parseCombo(s: string | null | undefined): Combo | null {
  if (!s) return null;
  const parts = s.split('+');
  const code = parts.pop();
  if (!code || MODIFIER_CODES.has(code)) return null;
  const c: Combo = { ctrl: false, alt: false, shift: false, meta: false, code };
  for (const p of parts) {
    if (p === 'Ctrl') c.ctrl = true;
    else if (p === 'Alt') c.alt = true;
    else if (p === 'Shift') c.shift = true;
    else if (p === 'Meta') c.meta = true;
    else return null;
  }
  return c;
}

export function formatCombo(c: Combo): string {
  const mods = [c.ctrl && 'Ctrl', c.alt && 'Alt', c.shift && 'Shift', c.meta && 'Meta'];
  return [...mods.filter((m): m is string => !!m), c.code].join('+');
}

/** The combination a key press makes; null for a modifier alone (still recording). */
export function comboOfEvent(e: KeyboardEvent): string | null {
  if (!e.code || MODIFIER_CODES.has(e.code)) return null;
  return formatCombo({
    ctrl: e.ctrlKey,
    alt: e.altKey,
    shift: e.shiftKey,
    meta: e.metaKey,
    code: e.code,
  });
}

const isMac = () => typeof navigator !== 'undefined' && /Mac/.test(navigator.platform ?? '');

/** "Ctrl + Shift + M" for people. */
export function comboLabel(s: string | null | undefined): string {
  const c = parseCombo(s);
  if (!c) return '';
  const mods = [
    c.ctrl && 'Ctrl',
    c.alt && (isMac() ? 'Option' : 'Alt'),
    c.shift && 'Shift',
    c.meta && (isMac() ? 'Cmd' : 'Win'),
  ];
  return [...mods.filter((m): m is string => !!m), keyLabel(c.code)].join(' + ');
}

export function comboMatches(e: KeyboardEvent, c: Combo): boolean {
  return (
    e.code === c.code &&
    e.ctrlKey === c.ctrl &&
    e.altKey === c.alt &&
    e.shiftKey === c.shift &&
    e.metaKey === c.meta
  );
}

const BROWSER_RESERVED = new Set(['KeyN', 'KeyT', 'KeyW', 'Tab', 'PageUp', 'PageDown']);

/**
 * A combination the system or the browser keeps for itself, so a page never
 * sees it (or sees it after the browser acted): the Windows key's, Alt+F4,
 * Alt+Tab, Ctrl+Alt+Delete and, in a browser, Ctrl(+Shift)+N, T, W, Tab,
 * Page Up and Page Down. Settings warns; the binding is kept.
 */
export function reservedCombo(s: string | null | undefined, desktop: boolean): boolean {
  const c = parseCombo(s);
  if (!c) return false;
  if (c.meta) return true;
  if (c.alt && !c.ctrl && (c.code === 'F4' || c.code === 'Tab')) return true;
  if (c.ctrl && c.alt && c.code === 'Delete') return true;
  if (desktop) return false;
  return c.ctrl && !c.alt && BROWSER_RESERVED.has(c.code);
}

let paused = 0;

/** While a keybind is being recorded, no keybind runs. Returns the resume. */
export function pauseHotkeys(): () => void {
  paused++;
  let done = false;
  return () => {
    if (done) return;
    done = true;
    paused--;
  };
}

/** The same action from the window and the shell's hook within this long counts once. */
export const HOTKEY_DEDUPE_MS = 300;

export type DesktopHotkeyBridge = Pick<NeosocialDesktopBridge, 'setHotkeys' | 'onHotkey'>;

export interface HotkeyOptions {
  keys: HotkeyKeys;
  run(action: HotkeyAction): void;
  /** The shell's global hook (desktop, `desktop_global_ptt` on). */
  bridge?: DesktopHotkeyBridge | undefined;
  target?: Window;
  now?: () => number;
}

/** Binds the keybinds; returns the unbind. */
export function bindHotkeys(o: HotkeyOptions): () => void {
  const now = o.now ?? Date.now;
  // the other source's report of the same press is skipped; two presses
  // from one source always count
  const last: Partial<Record<HotkeyAction, { at: number; from: 'window' | 'hook' }>> = {};
  const fire = (a: HotkeyAction, from: 'window' | 'hook') => {
    if (paused) return;
    const at = now();
    const prev = last[a];
    if (prev && prev.from !== from && at - prev.at < HOTKEY_DEDUPE_MS) return;
    last[a] = { at, from };
    o.run(a);
  };
  const combos: Array<[HotkeyAction, Combo]> = [];
  for (const a of HOTKEY_ACTIONS) {
    const c = parseCombo(o.keys[a]);
    if (c) combos.push([a, c]);
  }
  const w = o.target ?? window;
  const onKey = (e: KeyboardEvent) => {
    if (e.repeat || paused) return;
    for (const [a, c] of combos) {
      if (!comboMatches(e, c)) continue;
      // a bare key (or Shift and a key) would type in a field: not there
      if (!(c.ctrl || c.alt || c.meta) && isTypingTarget(e.target)) return;
      // the browser's own use of it (Ctrl+Shift+M, Ctrl+Shift+D) gives way
      e.preventDefault();
      fire(a, 'window');
      return;
    }
  };
  w.addEventListener('keydown', onKey, true);
  let offHook: (() => void) | undefined;
  const b = o.bridge;
  if (b?.setHotkeys && b.onHotkey) {
    b.setHotkeys({ ...o.keys }).catch((err: unknown) => {
      console.warn('the desktop app refused the keybinds:', err);
    });
    offHook = b.onHotkey((a) => {
      if (HOTKEY_ACTIONS.includes(a)) fire(a, 'hook');
    });
  }
  return () => {
    w.removeEventListener('keydown', onKey, true);
    offHook?.();
  };
}
